import { SwitchGroup } from '@augmentt-dev/ui-components'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PERMISSION_LEVELS } from '../../../../../../util/userRoles.util'
import PermissionAlert from '../PermissionAlert/PermissionAlert'

const SWITCH_GROUP_WIDTH = 270
const SYSTEM_USERS_MODULE = 'systemUsers'

/**
 * One product's permission switch, with its group heading and the warning for managing system users.
 */
function PermissionRow({ mapping, permission, levelOptions, value, isOwner = false, onChange }) {
  const intl = useIntl()
  const hasReadOnlyOption = permission.permission_options?.includes(PERMISSION_LEVELS.READ_ONLY)

  const options = useMemo(
    () =>
      hasReadOnlyOption ? levelOptions : levelOptions.filter((option) => option.value !== PERMISSION_LEVELS.READ_ONLY),
    [hasReadOnlyOption, levelOptions],
  )

  const handleChange = useCallback((level) => onChange(permission.id, level), [onChange, permission.id])

  const showsSystemUsersWarning =
    !isOwner && mapping.moduleName === SYSTEM_USERS_MODULE && value === PERMISSION_LEVELS.MANAGE

  return (
    <div className="permissionRow">
      {mapping.groupTitle && (
        <h6 className="permissionGroup">
          <FormattedMessage id={mapping.groupTitle} />
        </h6>
      )}

      <SwitchGroup
        title={intl.formatMessage({ id: mapping.title })}
        subTitle={mapping.subtitle ? intl.formatMessage({ id: mapping.subtitle }) : null}
        width={SWITCH_GROUP_WIDTH}
        disabled={isOwner}
        options={options}
        value={value}
        isSpaceBetween={!hasReadOnlyOption}
        setValue={handleChange}
      />

      {showsSystemUsersWarning && <PermissionAlert messageId="USERPERMISSION.systemUsersinfo" />}
    </div>
  )
}

PermissionRow.propTypes = {
  /** Entry of PERMISSION_MAPPINGS */
  mapping: PropTypes.shape({
    moduleName: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    groupTitle: PropTypes.string,
  }).isRequired,
  permission: PropTypes.shape({
    id: PropTypes.number.isRequired,
    permission_options: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  /** Every permission level as a switch option */
  levelOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })).isRequired,
  value: PropTypes.number,
  isOwner: PropTypes.bool,
  /** Called with the permission id and the chosen level */
  onChange: PropTypes.func.isRequired,
}

export default React.memo(PermissionRow)
